import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  SelectProps,
  Space,
  Switch,
  TreeSelect,
} from 'antd';
import Wrapper from '~/App/components/Wrapper';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createMenu,
  generateSequence,
  GetCategoryAndMenu,
  getCategoryAndMenu,
  getMenu,
  updateMenu,
} from '../../api';
import { useLoadingSetter } from '~/providers/LoadingProvider';
import message from '~/utils/message';
import { AddOn, Menu } from '../../types';

const { SHOW_CHILD } = TreeSelect;

const EditMenu = () => {
  const setLoading = useLoadingSetter();

  // Nav.
  const nav = useNavigate();
  const { menuId } = useParams<{ menuId: string }>();

  // Form.
  const [form] = Form.useForm();

  // States.
  const [categoryOptions, setCategoryOptions] = useState<
    SelectProps['options']
  >([]);
  const [addOnOptions, setAddOnOptions] = useState<any[]>([]);
  const [generateSequenceDisabled, setGenerateSequenceDisabled] = useState(
    !menuId
  );

  useEffect(() => {
    fetchMenu();
    fetchCategoryAndMenu();
  }, []);

  const fetchMenu = async () => {
    if (!menuId) {
      form.setFieldsValue({ isActive: true });
      return;
    }

    // Hit API.
    setLoading(true);
    let data: Menu;
    try {
      const { data: menu } = await getMenu(menuId);
      data = menu;
    } catch (error: any) {
      message.error('Failed to fetch menu: ' + error);
      return;
    } finally {
      setLoading(false);
    }

    form.setFieldsValue(data);
    // Get add-on IDs.
    const addOnIds = data.addOns.map((addOn) => addOn.id);
    form.setFieldValue('addOnIds', addOnIds);
  };

  const fetchCategoryAndMenu = async () => {
    setLoading(true);

    // Hit API.
    let data: GetCategoryAndMenu[] = [];
    try {
      const { data: res } = await getCategoryAndMenu();
      data = res;
    } catch (error: any) {
      message.error('Failed to fetch category and menu: ' + error);
      setLoading(false);
      return;
    }

    let categoryOptions: SelectProps['options'] = [];
    let addOnOptions = [];
    for (let i = 0; i < data.length; i++) {
      categoryOptions.push({
        label: data[i].name,
        value: data[i].id,
      });

      let items = [];
      for (let j = 0; j < data[i].items.length; j++) {
        // Skip the current edited menu.
        if (data[i].items[j].id === menuId) {
          continue;
        }
        items.push({
          title: data[i].items[j].name,
          value: data[i].items[j].id,
          key: j,
        });
      }

      if (items.length === 0) {
        continue;
      }

      addOnOptions.push({
        title: data[i].name,
        value: data[i].id,
        key: i,
        children: items,
      });
    }
    setCategoryOptions(categoryOptions);
    setAddOnOptions(addOnOptions);
    setLoading(false);
  };

  const handleSelectCategory = () => {
    setGenerateSequenceDisabled(false);
  };

  const handleGenerateSeq = async () => {
    setLoading(true);
    try {
      const { data } = await generateSequence(form.getFieldValue('categoryId'));
      form.setFieldValue('seq', data);
    } catch (error: any) {
      message.error('Failed to generate sequence: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    // Prepare menu request.
    // Add-ons
    let addOns: AddOn[] = [];
    const addOnIds: string[] = form.getFieldValue('addOnIds') || [];
    for (const addOnId of addOnIds) {
      addOns.push({ id: addOnId });
    }
    // Menu.
    let menu: Menu = form.getFieldsValue(true);
    menu.addOns = addOns;

    // Hit API.
    setLoading(true);
    try {
      if (menu.id) {
        // Update.
        await updateMenu(menu);
      } else {
        // Insert.
        await createMenu(menu);
      }
    } catch (error: any) {
      message.error('Failed to create or update: ' + error);
      return;
    } finally {
      setLoading(false);
    }

    message.success('Success');
    nav('..');
  };

  const handleCancel = () => {
    nav('..');
  };

  return (
    <Wrapper>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="categoryId" label="Category">
          <Select options={categoryOptions} onSelect={handleSelectCategory} />
        </Form.Item>
        <Form.Item name="name" label="Menu Name">
          <Input />
        </Form.Item>
        <Form.Item name="price" label="Price">
          <InputNumber
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
            }
            parser={(value) => value!.replace(/\$\s?|(\.*)/g, '')}
            controls={false}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="Sequence"
          extra="Sequence is used to sort the menu in reporting and menu display. Click generate to get the next sequence number."
        >
          <Space>
            <Form.Item name="seq" noStyle>
              <InputNumber />
            </Form.Item>
            <Button
              type="primary"
              disabled={generateSequenceDisabled}
              onClick={handleGenerateSeq}
            >
              Generate
            </Button>
          </Space>
        </Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="addOnIds" label="Add-ons">
          <TreeSelect
            allowClear
            multiple
            showCheckedStrategy={SHOW_CHILD}
            showSearch
            treeData={addOnOptions}
            treeCheckable
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" style={{ width: 150 }} htmlType="submit">
              Save
            </Button>
            <Button style={{ width: 150 }} onClick={handleCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Wrapper>
  );
};

export default EditMenu;
